import ApexCharts from 'apexcharts';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CompanyAggregatedReport, ReportService } from './../../service/report.service';
import { MatIcon } from "@angular/material/icon";
import { MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanel, MatAccordion } from "@angular/material/expansion";
import { CommonModule, DecimalPipe } from '@angular/common';
import jsPDF from 'jspdf';
import { Company } from '../../models/company.model';
import { CompanyService } from '../../service/company.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ensureUnicodePdfFont } from '../../shared/pdf-unicode-font.util';

interface DimensionInfo {
  name: string;
  score: number;
  criticAnswers : string[];
  improvementAreas : string[];
}

// Payload usato per esportare il radar nel PDF con dimensioni reali.
interface RadarChartExportImage {
  imgURI: string;
  width: number;
  height: number;
}

type RadarOptionsMode = 'view' | 'export';

@Component({ 
  selector: 'app-report-and-results', 
  standalone: true, 
  templateUrl: './report-and-results.component.html', 
  styleUrl: './report-and-results.component.css',
  imports: [MatIcon, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanel, MatAccordion,DecimalPipe,CommonModule, TranslateModule] 
}) 

export class ReportAndResultsComponent implements OnInit, AfterViewInit, OnDestroy {
  // Dati del report aggregato con dimensioni, punteggi e analisi domande
  dimensionsInfo: DimensionInfo[] = [];
  dimensionNames: string[] = [];
  dimensionScores: number[] = [];

  report: CompanyAggregatedReport | null = null;
  radarChart: ApexCharts | null = null;
  radarChartHost: HTMLElement | null = null;
  company : Company | null = null;
  private langChangeSub: Subscription | null = null;

  @ViewChild('radarChartHost')
  set radarChartContainer(element: ElementRef<HTMLDivElement> | undefined) {
    this.radarChartHost = element?.nativeElement ?? null;
    this.tryRenderRadarChart();
  }

  constructor(private reportService: ReportService, private companyService : CompanyService, private translate: TranslateService) {}
    
  // Carica report e dati aziendali, ricarica chart al cambio lingua
  ngOnInit(): void {
    this.reportService.getReport().subscribe({
          next: (res) => {
            this.report = res.selfAssessmentReport;
            this.loadDimensionInfo();
            this.tryRenderRadarChart();
          },
          error: (err) => console.error(err)
    });
    this.companyService.getCompany().subscribe({
      next: (res) => {
        this.company = res;
      }
    });

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.tryRenderRadarChart();
    });

  }

  // Estrae e organizza dati dimensioni dal report
  loadDimensionInfo() {
    if (!this.report) {
      this.dimensionNames = [];
      this.dimensionScores = [];
      this.dimensionsInfo = [];
      return;
    }

    this.dimensionNames = Object.keys(this.report.dimensionScores);
    this.dimensionScores = Object.values(this.report.dimensionScores);
    const dimensions = this.report.dimensions ?? {};

    this.dimensionsInfo = this.dimensionNames.map((name, index) => {
      const dimensionDetail = dimensions[name];
      return {
        name,
        score: this.dimensionScores[index] ?? 0,
        criticAnswers: dimensionDetail?.criticalIssues ?? [],
        improvementAreas: dimensionDetail?.improvementAreas ?? []
      };
    });

  }

  
  // Traduce nome dimensione usando chiave i18n dal backend
  translateDimensionName(name: string): string {
    const code = this.report?.dimensionTranslationKeys?.[name];
    if (!code) return name;
    const translated = this.translate.instant(code);
    return translated !== code ? translated : name;
  }
  ngAfterViewInit(): void {
    this.tryRenderRadarChart();
  }

  ngOnDestroy(): void {
    this.radarChart?.destroy();
    this.radarChart = null;
    this.langChangeSub?.unsubscribe();
    this.langChangeSub = null;
  }

  private async resolvePdfFontFamily(doc: jsPDF): Promise<string> {
    try {
      return await ensureUnicodePdfFont(doc);
    } catch (error) {
      console.error('Unable to load Unicode PDF font, falling back to Helvetica', error);
      return 'helvetica';
    }
  }

  private tryRenderRadarChart(): void {
    if (!this.report || !this.radarChartHost) return;
    this.renderRadarChart();
  }

  // Restituisce le categorie del radar, preferendo quelle gia calcolate.
  // Ottiene nomi dimensioni tradotti per il radar chart
  private getDimensionCategories(): string[] {
    if (this.dimensionNames.length) {
      return this.dimensionNames.map((name) => this.translateDimensionName(name));
    }

    return Object.keys(this.report?.dimensionScores ?? {}).map((name) => this.translateDimensionName(name));
  }

  // Normalizza i valori del radar in numeri validi per evitare NaN nel chart/PDF.
  private getDimensionValues(): number[] {
    const values = this.dimensionScores.length
      ? this.dimensionScores
      : Object.values(this.report?.dimensionScores ?? {});

    return values.map((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    });
  }

  private wrapRadarAxisLabel(label: string, maxCharsPerLine: number): string[] {
    const normalized = String(label ?? '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return [''];
    }

    const words = normalized.split(' ');
    if (normalized.length <= maxCharsPerLine || words.length <= 2) {
      return [normalized];
    }

    // Prefer splitting around conjunctions near the middle for natural two-line labels.
    const connectorIndexes = words
      .map((word, index) => ({ word: word.toLowerCase(), index }))
      .filter(({ word, index }) => index > 0 && index < words.length - 1 && ['and', 'e', 'et', 've', 'και', 'i', 'y'].includes(word))
      .map(({ index }) => index);

    let splitIndex = -1;
    if (connectorIndexes.length) {
      splitIndex = connectorIndexes.reduce((closest, current) => {
        const middle = (words.length - 1) / 2;
        return Math.abs(current - middle) < Math.abs(closest - middle) ? current : closest;
      }, connectorIndexes[0]);
    } else {
      // Fallback to the most balanced split by text length.
      let bestPenalty = Number.POSITIVE_INFINITY;
      for (let i = 1; i < words.length - 1; i++) {
        const left = words.slice(0, i + 1).join(' ');
        const right = words.slice(i + 1).join(' ');
        const penalty = Math.abs(left.length - right.length);
        if (penalty < bestPenalty) {
          bestPenalty = penalty;
          splitIndex = i;
        }
      }
    }

    if (splitIndex <= 0 || splitIndex >= words.length - 1) {
      return [normalized];
    }

    const firstLine = words.slice(0, splitIndex + 1).join(' ');
    const secondLine = words.slice(splitIndex + 1).join(' ');

    return [firstLine, secondLine];
  }

  // Configurazione unica del radar condivisa tra vista pagina ed export PDF.
  // Costruisce config ApexCharts radar, responsivo a modalità view/export e dispositivo
  private buildRadarChartOptions(mode: RadarOptionsMode = 'view'): any {
    const dimensionCategories = this.getDimensionCategories();
    const longestDimensionLabel = dimensionCategories.reduce((max, current) => Math.max(max, String(current ?? '').trim().length), 0);
    const containerWidth = mode === 'export'
      ? 980
      : (this.radarChartHost?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1024));
    const isMobile = mode === 'view' && containerWidth <= 768;
    const isTablet = mode === 'view' && containerWidth <= 1024;
    const hasLongLabels = longestDimensionLabel >= 20;
    const labelFontSize = mode === 'export'
      ? '18px'
      : (hasLongLabels ? (isMobile ? '9px' : '10px') : (isMobile ? '10px' : '11px'));
    const dataLabelFontSize = mode === 'export' ? '16px' : (isMobile ? '11px' : '10px');
    const markerSize = mode === 'export' ? 6 : (isMobile ? 4 : 4);
    const gridHorizontalPadding = mode === 'export'
      ? 24
      : (hasLongLabels ? (isMobile ? 52 : (isTablet ? 72 : 88)) : (isMobile ? 30 : (isTablet ? 40 : 52)));
    const gridVerticalPadding = mode === 'export' ? 10 : (hasLongLabels ? (isMobile ? 38 : 44) : (isMobile ? 26 : 30));
    const axisLabelMaxChars = mode === 'export' ? 28 : (isMobile ? 18 : 26);
    const wrappedViewLabels = mode === 'view'
      ? dimensionCategories.map((label) => this.wrapRadarAxisLabel(label, axisLabelMaxChars))
      : [];
    const maxWrappedLines = mode === 'view'
      ? wrappedViewLabels.reduce((max, labelLines) => Math.max(max, labelLines.length), 1)
      : 1;
    const dynamicMinHeight = mode === 'export'
      ? 20
      : Math.min(150, Math.max(isMobile ? 62 : 52, maxWrappedLines * (isMobile ? 12 : 13) + 22));

    return {
      chart: { type: 'radar', height: '100%', width: '100%', toolbar: { show: false } },
      series: [{
        name: this.translate.instant('report-and-results.chart-series-name'),
        data: this.getDimensionValues()
      }],
      yaxis: {
        min: 0,
        max: 5,
        tickAmount: 5,
        labels: {
          show: mode === 'export',
          style: {
            colors: '#5f5f5f',
            fontSize: mode === 'export' ? '14px' : (isMobile ? '10px' : '10px'),
            fontFamily: 'Roboto'
          }
        }
      },
      xaxis: {
        categories: mode === 'view' ? wrappedViewLabels : dimensionCategories,
        labels: {
          show: true,
          trim: false,
          minHeight: dynamicMinHeight,
          offsetY: mode === 'view' ? (hasLongLabels ? -14 : -8) : 0,
          style: {
            colors: Array(dimensionCategories.length).fill("#5f5f5f"),
            fontSize: labelFontSize,
            fontFamily: 'Roboto',
            whiteSpace: 'normal',
            textAlign: 'center'
          }
        }
      },
      plotOptions: {
        radar: {
          size: mode === 'view'
            ? (hasLongLabels ? (isMobile ? 72 : (isTablet ? 88 : 102)) : (isMobile ? 108 : (isTablet ? 124 : 140)))
            : undefined,
          polygons: {
            strokeColor: '#e8e8e8',
            fill: { colors: ['#f8f8f8', '#fff'] }
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetY: mode === 'view' && isMobile ? 1 : 0,
        style: { colors: ['#1a9bfc'], fontSize: dataLabelFontSize, fontWeight: 600 },
        background: {
          enabled: true,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: '#ffffff',
          opacity: 0.95,
          padding: mode === 'view' && isMobile ? 6 : 4
        }
      },
      fill: { opacity: 0.2 },
      stroke: { width: 1 },
      markers: { size: markerSize },
      grid: {
        padding: {
          top: gridVerticalPadding,
          bottom: gridVerticalPadding,
          left: gridHorizontalPadding,
          right: gridHorizontalPadding
        }
      }
    };
  }

  // Estrae la data URI PNG dall'output ApexCharts.dataURI.
  private extractRadarChartImageUri(dataUriResult: { imgURI?: string } | { blob?: Blob } | null): string | null {
    if (dataUriResult && "imgURI" in dataUriResult && typeof dataUriResult.imgURI === "string" && dataUriResult.imgURI.length > 0) {
      return dataUriResult.imgURI;
    }

    return null;
  }

  // Legge width/height reali dell'immagine per mantenere il corretto aspect ratio nel PDF.
  private async resolveImageDimensions(imgURI: string): Promise<{ width: number; height: number } | null> {
    if (typeof Image === "undefined") {
      return null;
    }

    return new Promise((resolve) => {
      const image = new Image();

      image.onload = () => {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;

        if (width > 0 && height > 0) {
          resolve({ width, height });
        } else {
          resolve(null);
        }
      };

      image.onerror = () => resolve(null);
      image.src = imgURI;
    });
  }

  // Costruisce il payload completo del radar esportato (URI + dimensioni native).
  private async buildRadarChartExportImage(dataUriResult: { imgURI?: string } | { blob?: Blob } | null): Promise<RadarChartExportImage | null> {
    const imgURI = this.extractRadarChartImageUri(dataUriResult);
    if (!imgURI) {
      return null;
    }

    const imageDimensions = await this.resolveImageDimensions(imgURI);
    return {
      imgURI,
      width: imageDimensions?.width ?? 1,
      height: imageDimensions?.height ?? 1
    };
  }

  // Genera l'immagine del radar per il PDF usando sempre un chart offscreen
  // con dimensioni fisse, cosi l'export non dipende dal device.
  // Renderizza radar offscreen con dimensioni fisse per export PDF coerente
  private async getRadarChartImageUri(): Promise<RadarChartExportImage | null> {
    if (!this.report || typeof document === "undefined") {
      return null;
    }

    const temporaryContainer = document.createElement("div");
    temporaryContainer.style.position = "fixed";
    temporaryContainer.style.left = "-10000px";
    temporaryContainer.style.top = "0";
    temporaryContainer.style.width = "980px";
    temporaryContainer.style.height = "760px";
    temporaryContainer.style.pointerEvents = "none";
    temporaryContainer.style.opacity = "0";
    temporaryContainer.style.background = "#ffffff";
    document.body.appendChild(temporaryContainer);

    let temporaryChart: ApexCharts | null = null;

    try {
      const options = this.buildRadarChartOptions('export');
      options.chart = {
        ...(options.chart ?? {}),
        height: 760,
        width: 980,
        toolbar: { show: false },
        animations: { enabled: false }
      };

      temporaryChart = new ApexCharts(temporaryContainer, options);
      await temporaryChart.render();

      const temporaryChartData = await temporaryChart.dataURI({ width: 1200 });
      return this.buildRadarChartExportImage(temporaryChartData);
    } catch (error) {
      console.error("Error generating fallback radar chart image", error);
      return null;
    } finally {
      temporaryChart?.destroy();
      temporaryContainer.remove();
    }
  }

  renderRadarChart() {
    if (!this.report || !this.radarChartHost) return;

    this.radarChart?.destroy();
    this.radarChart = null;

    const options = this.buildRadarChartOptions('view');

    this.radarChart = new ApexCharts(this.radarChartHost, options);
    this.radarChart.render();
  }

  // Export completo del report: header/footer, intro, radar e analisi dimensioni.
  // Genera PDF completo: header, radar chart, analisi dimensioni con criticità e miglioramenti
  async exportPdf(): Promise<void> {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pdfFontFamily = await this.resolvePdfFontFamily(doc);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const contentWidth = pageWidth - marginX * 2;
    const listWidth = contentWidth - 10;
    const headerContentStartY = 38;
    const footerReserve = 26;
    const contentBottomY = pageHeight - footerReserve;
    let currentY = headerContentStartY;

    const dimensionScores: Record<string, number> = this.report?.dimensionScores ?? {};
    const dimensions: Record<string, { criticalIssues?: string[]; improvementAreas?: string[] }> = this.report?.dimensions ?? {};
    const companyName = String(this.company?.name ?? "").trim();
    const reportDate = new Date().toLocaleDateString("en-US");
    const logoPath = "/img/logo_blue.png";
    const euLogoPath = "/img/eu_co_funded_en.jpg";
    const footerText = this.translate.instant('inclusion-index.pdf.footer-disclaimer');
    const projectNumber = "2021-1-IT02-KA220-VET-000028169";

    // Disegna header con loghi, titolo, azienda e data
    const drawPageHeader = () => {
      try {
        doc.addImage(logoPath, "PNG", marginX, 6, 34, 9);
      } catch (error) {
        console.error("Error adding left logo", error);
      }

      try {
        doc.addImage(euLogoPath, "PNG", pageWidth - marginX - 48, 6, 48, 9);
      } catch (error) {
        console.error("Error adding EU logo", error);
      }

      doc.setDrawColor(214, 223, 239);
      doc.line(marginX, 19, pageWidth - marginX, 19);

      doc.setFont(pdfFontFamily, "bold");
      doc.setFontSize(13);
      doc.setTextColor(20, 32, 61);
      doc.text(this.translate.instant('report-and-results.title'), marginX, 26);

      doc.setFont(pdfFontFamily, "normal");
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      doc.text(companyName || "Company", marginX, 31);
      doc.text(this.translate.instant('report-and-results.pdf.date', {date: reportDate}), pageWidth - marginX, 31, { align: "right" });

      currentY = headerContentStartY;
    };

    // Disegna footer con disclaimer, numero progetto e contatore pagine
    const drawPageFooter = (page: number, totalPages: number) => {
      const footerLines = doc.splitTextToSize(footerText, pageWidth - marginX * 2);
      const footerLineHeight = 3.1;
      const projectY = pageHeight - 6;
      const disclaimerY = projectY - 4 - ((footerLines.length - 1) * footerLineHeight);

      doc.setFontSize(7);
      doc.setTextColor(80, 80, 80);
      doc.setFont(pdfFontFamily, "normal");
      doc.text(footerLines, marginX, disclaimerY);

      doc.setFont(pdfFontFamily, "bold");
      doc.text(this.translate.instant('inclusion-index.pdf.project-number-label'), marginX, projectY);
      doc.setFont(pdfFontFamily, "normal");
      doc.text(projectNumber, marginX + 23, projectY);
      doc.text(this.translate.instant('report-and-results.pdf.page', {page, totalPages}), pageWidth - marginX, projectY, { align: "right" });
    };

    const ensureSpace = (requiredHeight: number) => {
      if (currentY + requiredHeight > contentBottomY) {
        doc.addPage();
        drawPageHeader();
      }
    };

    const splitLines = (value: string, maxWidth: number) => {
      const lines = doc.splitTextToSize(value, maxWidth);
      return Array.isArray(lines) ? lines : [String(lines)];
    };

    // Calcola un rettangolo "contain": l'immagine entra nel box senza deformarsi.
    const getContainedImageRect = (
      sourceWidth: number,
      sourceHeight: number,
      maxWidth: number,
      maxHeight: number
    ) => {
      const safeSourceWidth = sourceWidth > 0 ? sourceWidth : maxWidth;
      const safeSourceHeight = sourceHeight > 0 ? sourceHeight : maxHeight;
      const scale = Math.min(maxWidth / safeSourceWidth, maxHeight / safeSourceHeight);
      const width = safeSourceWidth * scale;
      const height = safeSourceHeight * scale;
      return { width, height };
    };

    const toLines = (value: string) => splitLines(value, listWidth);

    const estimateLinesCount = (items: string[]) => {
      if (!items.length) return 1;
      return items.reduce((total, item) => total + toLines(`- ${String(item)}`).length, 0);
    };

    // Scrive sezione lista formattata: titolo, sottotitolo, elementi domande tradotte
    const writeList = (title: string, subtitle: string, items: string[]) => {
      ensureSpace(7);
      doc.setFont(pdfFontFamily, "bold");
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      doc.text(title, marginX + 2, currentY);
      currentY += 5;

      if (subtitle) {
        const subtitleLines = toLines(subtitle);
        ensureSpace(subtitleLines.length * 5 + 2);
        doc.setFont(pdfFontFamily, "italic");
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 100);
        doc.text(subtitleLines, marginX + 2, currentY);
        currentY += subtitleLines.length * 5 + 2;
      }

      doc.setFont(pdfFontFamily, "normal");
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);

      if (!items.length) {
        ensureSpace(6);
        doc.text(this.translate.instant('report-and-results.pdf.no-items'), marginX + 6, currentY);
        currentY += 5;
        return;
      }

      items.forEach((item) => {
        const normalizedLines = toLines(`- ${String(item)}`);
        ensureSpace(normalizedLines.length * 5 + 2);
        doc.text(normalizedLines, marginX + 6, currentY);
        currentY += normalizedLines.length * 5;
      });
    };

    drawPageHeader();

    const inclusionIndexValue = Number(this.report?.inclusionIndex);
    const inclusionIndexText = Number.isFinite(inclusionIndexValue) ? inclusionIndexValue.toFixed(2) : "N/A";
    const inclusionLevelText = this.report?.inclusionLevel ? String(this.report.inclusionLevel) : "N/A";
    const letterGradeText = this.report?.letterGrade ? String(this.report.letterGrade) : "N/A";

    const introText = this.translate.instant('report-and-results.intro', {
      inclusionIndex: inclusionIndexText,
      inclusionLevel: inclusionLevelText,
      letterGrade: letterGradeText
    });
    const introLines = splitLines(introText, contentWidth - 8);
    const introLineHeight = 4.6;
    const introPaddingY = 3;
    const introBoxHeight = introPaddingY * 2 + (introLines.length * introLineHeight);
    ensureSpace(introBoxHeight + 6);

    doc.setFillColor(22, 22, 64);
    doc.roundedRect(marginX, currentY - 3, contentWidth, introBoxHeight, 2, 2, "F");
    doc.setFont(pdfFontFamily, "normal");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(introLines, marginX + 4, currentY + 1);
    currentY += introBoxHeight + 4;

    const radarChartImageData = await this.getRadarChartImageUri();

    ensureSpace(8);
    doc.setFont(pdfFontFamily, "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 32, 61);
    doc.text(this.translate.instant('report-and-results.company-inclusion-index'), marginX + 2, currentY);
    currentY += 4;

    if (radarChartImageData) {
      const chartContainerHeight = 140;
      ensureSpace(chartContainerHeight + 4);

      const chartPadding = 3;
      const chartBoxX = marginX;
      const chartBoxY = currentY;
      const chartBoxWidth = contentWidth;
      const chartBoxHeight = chartContainerHeight;
      const availableChartWidth = chartBoxWidth - (chartPadding * 2);
      const availableChartHeight = chartBoxHeight - (chartPadding * 2);
      const chartImageRect = getContainedImageRect(
        radarChartImageData.width,
        radarChartImageData.height,
        availableChartWidth,
        availableChartHeight
      );
      const chartX = chartBoxX + ((chartBoxWidth - chartImageRect.width) / 2);
      const chartY = chartBoxY + ((chartBoxHeight - chartImageRect.height) / 2);

      doc.setDrawColor(226, 232, 240);
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(chartBoxX, chartBoxY, chartBoxWidth, chartBoxHeight, 2, 2, "FD");

      try {
        doc.addImage(radarChartImageData.imgURI, "PNG", chartX, chartY, chartImageRect.width, chartImageRect.height);
      } catch (error) {
        console.error("Error adding radar chart image to PDF", error);
        doc.setFont(pdfFontFamily, "italic");
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(this.translate.instant('report-and-results.pdf.chart-not-available'), marginX + 4, currentY + 8);
      }

      currentY += chartContainerHeight + 6;
    } else {
      ensureSpace(10);
      doc.setFont(pdfFontFamily, "italic");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(this.translate.instant('report-and-results.pdf.chart-not-available'), marginX + 2, currentY);
      currentY += 8;
    }

    const entries = Object.entries(dimensionScores);
    if (!entries.length) {
      doc.setFont(pdfFontFamily, "italic");
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(this.translate.instant('report-and-results.pdf.no-dimensions'), marginX, currentY);
    } else {
      ensureSpace(12);
      doc.setFont(pdfFontFamily, "bold");
      doc.setFontSize(13);
      doc.setTextColor(20, 32, 61);
      doc.text(this.translate.instant('report-and-results.detailed-analysis'), marginX + 2, currentY);
      currentY += 8;

      entries.forEach(([name, rawScore]) => {
        const detail = dimensions[name] ?? {};
        const criticalIssues = Array.isArray(detail.criticalIssues) ? detail.criticalIssues : [];
        const improvementAreas = Array.isArray(detail.improvementAreas) ? detail.improvementAreas : [];

        const criticalLines = estimateLinesCount(criticalIssues);
        const improvementLines = estimateLinesCount(improvementAreas);
        const requiredHeight = 14 + 40 + 5 + (criticalLines * 5) + 5 + (improvementLines * 5) + 6;

        ensureSpace(requiredHeight);

        doc.setFillColor(239, 244, 255);
        doc.roundedRect(marginX, currentY - 4, contentWidth, 8, 1.4, 1.4, "F");
        doc.setFont(pdfFontFamily, "bold");
        doc.setFontSize(11);
        doc.setTextColor(20, 32, 61);
        doc.text(this.translateDimensionName(String(name)), marginX + 2, currentY + 1);

        const numericScore = Number(rawScore);
        const scoreText = Number.isFinite(numericScore) ? numericScore.toFixed(2) : String(rawScore);
        const scoreColor = Number.isFinite(numericScore)
          ? (numericScore >= 4 ? [22, 163, 74] : numericScore >= 2.5 ? [217, 119, 6] : [220, 38, 38])
          : [30, 41, 59];
        doc.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
        doc.text(this.translate.instant('report-and-results.pdf.score', {score: scoreText}), marginX + contentWidth - 2, currentY + 1, { align: "right" });
        currentY += 10;

        const summaryText = this.translate.instant('report-and-results.dimension-summary', {
          name: this.translateDimensionName(String(name)),
          score: scoreText
        });
        const summaryLines = toLines(summaryText);
        ensureSpace(summaryLines.length * 5 + 4);
        doc.setFont(pdfFontFamily, "normal");
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        doc.text(summaryLines, marginX + 2, currentY);
        currentY += summaryLines.length * 5 + 4;

        writeList(
          this.translate.instant('report-and-results.dimension-criticalities-title'),
          this.translate.instant('report-and-results.dimension-criticalities-subtitle'),
          criticalIssues.map(key => this.translate.instant(key))
        );
        writeList(
          this.translate.instant('report-and-results.dimension-improvements-title'),
          this.translate.instant('report-and-results.dimension-improvements-subtitle'),
          improvementAreas.map(key => this.translate.instant(key))
        );

        doc.setDrawColor(226, 232, 240);
        doc.line(marginX, currentY, marginX + contentWidth, currentY);
        currentY += 6;
      });
    }

    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      drawPageHeader();
      drawPageFooter(page, totalPages);
    }

    doc.save("report-and-analysis.pdf");
  }



  /* 
  private readonly dimensionTranslationMap: Record<string, string> = {
    leadership: 'report-and-results.dimension-name.leadership',
    comunicazione: 'report-and-results.dimension-name.communication',
    communication: 'report-and-results.dimension-name.communication',
    diversita: 'report-and-results.dimension-name.diversity',
    diversity: 'report-and-results.dimension-name.diversity',
    sviluppo: 'report-and-results.dimension-name.development',
    development: 'report-and-results.dimension-name.development',
    supporto: 'report-and-results.dimension-name.support',
    support: 'report-and-results.dimension-name.support'
  };

  translateDimensionName(name: string): string {
    const normalized = String(name ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

    const translationKey = this.dimensionTranslationMap[normalized];
    if (!translationKey) {
      return name;
    }

    const translated = this.translate.instant(translationKey);
    return translated && translated !== translationKey ? translated : name;
  }
  ngAfterViewInit(): void {
    const options = {
      plotOptions: {
        radar: {
          polygons: {
            strokeColor: '#e8e8e8',
            fill: {
                colors: ['#f8f8f8', '#fff']
            }
          }
        }
      }
      ,
      chart: {
        type: 'radar',
        height: '100%',
        width: '100%',
        toolbar: { show: false },
      },
      grid: {
        padding: {
          top: 0,
          bottom: 0,
          left: 0,
          right: 0
        }
      },
      series: [{
        name: 'Inclusione',
        data: [3, 4, 2, 5, 3]  
      }],
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          borderRadius:2,
        }
      },
      xaxis: {
      categories: ['Leadership', 'Comunicazione', 'Diversità', 'Sviluppo', 'Supporto'],
      labels: {
        show: true,
        style: {
          colors: ["#5f5f5fff"],
          fontSize: "12px",
          fontFamily: 'Roboto'
        }
      }
      },
      fill: {
        opacity: 0.2
      },
      stroke: {
        width: 1
      },
      markers: {
        size: 4,
        hover: {
          size: 10
        }
      }
    };

    this.radarChart = new ApexCharts(document.querySelector("#radarChart")!, options);
    this.radarChart.render();
  } */
}
